﻿/**
 * Directiva Vue: v-decimal-input
 *
 * Permite que inputs numericos acepten tanto coma (,) como punto (.) como
 * separador decimal, convirtiendo automaticamente la coma a punto.
 *
 * Compatible con:
 *  - PrimeVue InputNumber (mode="currency" o "decimal", locale="en-US")
 *  - Plain HTML <input> (para campos como vatValue)
 *
 * ## Por que execCommand y no un evento sintetico
 *
 * Despachar un `new KeyboardEvent('keydown', {key: '.'})` sintetico NO inserta
 * ningun caracter: los navegadores solo ejecutan la insercion de texto para
 * eventos de teclado REALES (isTrusted=true). PrimeVue permite el '.' en su
 * handler de keydown (no llama preventDefault), pero el navegador no inserta
 * nada con el evento sintetico.
 *
 * `document.execCommand('insertText', false, '.')` SI inserta el texto en la
 * posicion actual del cursor, dispara beforeinput+input nativo, y el handler
 * onInput de PrimeVue procesa el resultado correctamente.
 *
 * ## Estrategia de listeners
 *
 * - keydown: burbuja (PrimeVue solo llama preventDefault para ',', sin efectos secundarios)
 * - paste  : captura (necesario para interceptar antes que el onPaste de PrimeVue)
 *
 * Uso:
 *   <InputNumber v-decimal-input v-model="form.price" ... />
 *   <input v-decimal-input v-model="form.vatValue" ... />
 */

import type { Directive } from 'vue';

// --- Helpers ----------------------------------------------------------------

/**
 * Inserta texto en la posicion actual del cursor del input.
 *
 * Primero intenta execCommand (dispara beforeinput+input nativo, compatible con
 * PrimeVue InputNumber). Si no esta disponible, usa setRangeText + evento input.
 */
function insertAtCursor(input: HTMLInputElement, text: string): void {
  // execCommand opera sobre el elemento enfocado -- el input siempre esta
  // enfocado cuando el usuario escribe o pega en el.
  if (document.execCommand('insertText', false, text)) return;

  // Fallback: setRangeText + evento input sintetico
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
  if (nativeSetter) {
    nativeSetter.call(input, input.value.slice(0, start) + text + input.value.slice(end));
    input.setSelectionRange(start + text.length, start + text.length);
  } else {
    input.setRangeText(text, start, end, 'end');
  }
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Normaliza texto pegado: reemplaza separador decimal coma -> punto.
 *
 * Detecta formato europeo ("1.234,56" -> "1234.56") y coma simple ("1,5" -> "1.5").
 */
function normalizeDecimalText(text: string): string {
  let s = text.trim();

  // Formato europeo: punto de miles + coma decimal -> quitar puntos + coma->punto
  if (/\d\.\d{3},/.test(s)) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    s = s.replace(/,/g, '.');
  }

  // Dejar solo digitos, punto y signo negativo inicial
  s = s.replace(/[^0-9.\-]/g, '');

  // Maximo un punto decimal (conservar el ultimo)
  const parts = s.split('.');
  if (parts.length > 2) {
    s = parts.slice(0, -1).join('') + '.' + parts[parts.length - 1];
  }

  return s;
}

/** Obtiene el <input> nativo: el propio elemento si es INPUT, o el primero hijo. */
const getNativeInput = (el: HTMLElement): HTMLInputElement | null =>
  el.tagName === 'INPUT' ? (el as HTMLInputElement) : el.querySelector('input');

// --- Event handlers ---------------------------------------------------------

/**
 * Keydown: intercepta coma y punto para gestionar el separador decimal.
 *
 * Estrategia:
 *  - Si el input ya tiene '.' (valor formateado de PrimeVue como "$3.00"):
 *    MOVER el cursor al lugar justo despues del separador decimal, sin insertar nada.
 *    Insertar otro '.' causaria "$3..00" porque PrimeVue no limpia el intermedio.
 *  - Si el input NO tiene '.' todavia (input vacio o sin decimales):
 *    INSERTAR '.' via execCommand.
 *
 * Intercepta tambien '.' nativo (excluyendo NumpadDecimal que PrimeVue maneja solo)
 * para evitar que PrimeVue InputNumber permita multiples puntos por el mismo motivo.
 */
const keydownHandler = (event: KeyboardEvent) => {
  const isComma = event.key === ',';
  // Interceptar '.' solo para teclado regular; NumpadDecimal lo maneja PrimeVue internamente
  const isDot = event.key === '.' && event.code !== 'NumpadDecimal';
  if (!isComma && !isDot) return;

  event.preventDefault();
  const input = event.target as HTMLInputElement;
  const dotPos = input.value.indexOf('.');
  const hasSelection = input.selectionStart !== input.selectionEnd;

  if (dotPos >= 0 && !hasSelection) {
    // Ya existe separador decimal en el valor formateado: solo mover cursor despues de el
    input.setSelectionRange(dotPos + 1, dotPos + 1);
  } else {
    // Sin separador, o hay seleccion que reemplazar: insertar '.' (execCommand borra la seleccion)
    insertAtCursor(input, '.');
  }
};

/**
 * Paste: normaliza el texto pegado si contiene comas.
 *
 * Se registra en CAPTURA para interceptar antes que el onPaste de PrimeVue,
 * que de otro modo rechazaria la coma como separador invalido para en-US.
 */
const pasteHandler = (event: ClipboardEvent) => {
  const text = event.clipboardData?.getData('text') ?? '';
  if (!text.includes(',')) return; // Sin comas -> dejar que PrimeVue maneje el paste normal

  event.preventDefault();
  event.stopImmediatePropagation(); // Evitar que onPaste de PrimeVue reciba el texto con coma

  insertAtCursor(event.target as HTMLInputElement, normalizeDecimalText(text));
};

// --- Directiva --------------------------------------------------------------

function bind(input: HTMLInputElement): void {
  input.addEventListener('keydown', keydownHandler); // burbuja
  input.addEventListener('paste', pasteHandler, true); // captura
}

function unbind(input: HTMLInputElement): void {
  input.removeEventListener('keydown', keydownHandler);
  input.removeEventListener('paste', pasteHandler, true);
}

export const decimalInputDirective: Directive = {
  mounted(el: HTMLElement) {
    const input = getNativeInput(el);
    if (!input) return;

    bind(input);
    (el as any).__decimalDirective__ = { input };
  },

  updated(el: HTMLElement) {
    const stored = (el as any).__decimalDirective__;
    if (!stored) return;
    const input = getNativeInput(el);
    if (input && input !== stored.input) {
      unbind(stored.input);
      bind(input);
      stored.input = input;
    }
  },

  unmounted(el: HTMLElement) {
    const stored = (el as any).__decimalDirective__;
    if (stored?.input) unbind(stored.input);
    delete (el as any).__decimalDirective__;
  },
};

export default decimalInputDirective;
